import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Box, Checkbox, Typography, createFilterOptions } from '@mui/material'

import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material'
import { StateSchema } from '@/hooks/form/models'
import AutocompleteCustom from '@/views/components/autocomplete-custom/index.component'

import { ApplicationState } from '@/state/root.models'

type Props = {
  handleOnChangeDependencies: (name: string, values: any) => void
  state: StateSchema
}

const DependenciesComponent = ({
  state,
  handleOnChangeDependencies,
}: Props) => {
  const { t } = useTranslation()

  const { bonusesByBundle } = useSelector(
    ({ bonusEngine }: ApplicationState) => bonusEngine,
  )

  const allSelected = bonusesByBundle.length === state.bonusBundle.value.length

  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      handleOnChangeDependencies('bonusBundle', bonusesByBundle)
    } else {
      handleOnChangeDependencies('bonusBundle', [])
    }
  }

  const handleToggleSelectAll = () => {
    handleSelectAll && handleSelectAll(!allSelected)
  }

  const handleChange = (event: any, selectedOptions: any[], reason: string) => {
    if (reason === 'selectOption' || reason === 'removeOption') {
      if (selectedOptions.find((option) => option._id === 'select-all')) {
        handleToggleSelectAll()
      } else if (reason === 'removeOption') {
        handleOnChangeDependencies('bonusBundle', selectedOptions)
      } else {
        const findValue =
          selectedOptions.length > 0 &&
          state.bonusBundle.value.find(
            (item: any) =>
              item.name === selectedOptions[selectedOptions.length - 1].name,
          )

        if (findValue) {
          const removeValue = state.bonusBundle.value.filter(
            (provider: any) =>
              provider.name !==
              selectedOptions[selectedOptions.length - 1].name,
          )
          handleOnChangeDependencies('bonusBundle', removeValue)
        } else {
          handleOnChangeDependencies('bonusBundle', selectedOptions)
        }
      }
    } else if (reason === 'clear') {
      handleOnChangeDependencies('bonusBundle', [])
    }
  }

  const filter = createFilterOptions()

  return (
    <Box maxWidth={400}>
      <Typography fontWeight={700} sx={{ mb: '20px' }}>
        {t('bonusByBundle')}
      </Typography>
      <Typography variant="body2" gutterBottom>
        {t('bonusByBundleMessage')}
      </Typography>
      <AutocompleteCustom
        getOptionLabel={(option) => option.name}
        filterOptions={(options: any, params: any) => {
          const filtered = filter(options, params)
          return [
            { name: t('labels.selectAll'), _id: 'select-all' },
            ...filtered,
          ]
        }}
        limitTags={1}
        multiple
        onChange={handleChange}
        chipTags
        options={bonusesByBundle}
        label={t('messages.addBonusBundle')}
        value={state.bonusBundle.value || []}
        renderOption={(props, option) => {
          const selectAllProps =
            option._id === 'select-all' // To control the state of 'select-all' checkbox
              ? { checked: allSelected }
              : {}

          return (
            <li {...props}>
              <Checkbox
                color="primary"
                icon={<CheckBoxOutlineBlank fontSize="small" />}
                checkedIcon={<CheckBox fontSize="small" />}
                style={{ marginRight: 8 }}
                checked={
                  state.bonusBundle.value.filter(
                    (bonusBundle: any) => bonusBundle._id === option._id,
                  ).length > 0
                }
                {...selectAllProps}
              />
              {option._id === 'select-all' ? (
                <span className="select-all-items">
                  <em>{option.name}</em>
                </span>
              ) : (
                option.name
              )}
            </li>
          )
        }}
      />
    </Box>
  )
}

export default DependenciesComponent
